/**
 * Turret pattern 3 — the laser turret lock-on (legacy Avoid! attack #3, family
 * `turret`; docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * Legacy behaviour was a five-phase sequence: (1) move turrets in, (2) FREE-AIM —
 * each turret rotates toward the player via `atan2`, (3) charge for
 * `difficultytimer/2` (+charge sfx), (4) shoot for `difficultytimer/2` (+shoot sfx,
 * laser goes live), (5) retract. The number of turrets scales with difficulty
 * (1/2/3/4) — hardest at L5 with 4 turrets and a 500 ms charge/shoot.
 *
 * This generator only EMITS the turret beams. As the task spec requires, the world
 * stepper owns the aiming: phases 1–3 (move-in + free-aim + charge) are the harmless
 * TELEGRAPH during which the stepper continuously points each beam at the live
 * player position; phase 4 (shoot) is the lethal ACTIVE window during which the aim
 * is locked. So each turret is a {@link import('../types').SegmentHazard} whose
 * endpoint the stepper rewrites every step — here we just seed the anchor (turret
 * muzzle, pinned to a viewport edge) and a placeholder ray.
 *
 * Coordinates are VIEWPORT-RELATIVE. Turret anchors sit just outside the play area,
 * evenly spread around the perimeter so multiple turrets cross-fire.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { Hazard, SegmentHazard, Vec2 } from '../types';
import type { Pattern, PatternContext } from './types';

/** Beam collision width (px) — a thin lance, fraction of the smaller viewport side. */
const TURRET_BEAM_THICKNESS_FRACTION = 0.045;

/** How far past the edge each turret muzzle sits, as a fraction of the smaller side. */
const TURRET_INSET_FRACTION = 0.06;

/**
 * Fraction of the telegraph spent TRACKING the player before the aim locks. The
 * remaining `1 - this` is the CHARGE window: the aim is frozen on the player's
 * last-tracked position and the player can dodge OFF the locked line before the
 * beam fires. (Previously the beam tracked the WHOLE telegraph then fired exactly
 * where the player stood — unavoidable. This gives a real reaction window.)
 */
const TURRET_TRACK_FRACTION = 0.55;

/**
 * Turret count by difficulty level (legacy 1/2/3/4), capped at four. Tiers 4 and 5
 * both field the maximum four turrets.
 */
function turretCount(level: number): number {
  return Math.max(1, Math.min(4, level));
}

/**
 * The muzzle anchors for `n` turrets, pinned just outside the play area and spread
 * around the perimeter (top, bottom, left, right order) so they cross-fire. Returned
 * in a fixed order so selection stays deterministic.
 */
function turretAnchors(n: number, width: number, height: number, inset: number): Vec2[] {
  const candidates: Vec2[] = [
    { x: width * 0.5, y: -inset }, // top-centre
    { x: width * 0.5, y: height + inset }, // bottom-centre
    { x: -inset, y: height * 0.5 }, // left-centre
    { x: width + inset, y: height * 0.5 }, // right-centre
  ];
  return candidates.slice(0, n);
}

/** Build the turret beams for one lock-on attack. */
function makeTurrets(ctx: PatternContext): Hazard[] {
  const { viewport, difficulty, elapsedMs } = ctx;
  const { width, height } = viewport;
  const minSide = Math.min(width, height);

  const n = turretCount(difficulty.level);
  const inset = minSide * TURRET_INSET_FRACTION;
  const thickness = minSide * TURRET_BEAM_THICKNESS_FRACTION;

  // Telegraph = TRACK (re-aim at the player) then CHARGE (aim frozen, harmless) so
  // the player can dodge off the locked line; the active window is the lethal SHOOT.
  const telegraphMs = difficulty.attackSpeedMs;
  const activeMs = difficulty.attackSpeedMs * 0.5;
  const aimLockMs = telegraphMs * TURRET_TRACK_FRACTION;

  // The beam reaches across the whole play area; the stepper sets the far endpoint
  // by aiming at the player each step. Seed a default ray pointing at centre so the
  // descriptor is well-formed before the first aim update.
  const reach = Math.hypot(width, height);
  const cx = width * 0.5;
  const cy = height * 0.5;

  return turretAnchors(n, width, height, inset).map((anchor): SegmentHazard => {
    const dx = cx - anchor.x;
    const dy = cy - anchor.y;
    const len = Math.hypot(dx, dy) || 1;
    return {
      id: 0,
      family: 'turret',
      kind: 'segment',
      bornMs: elapsedMs,
      telegraphMs,
      activeMs,
      x1: anchor.x,
      y1: anchor.y,
      x2: anchor.x + (dx / len) * reach,
      y2: anchor.y + (dy / len) * reach,
      thickness,
      // Lock-on: the stepper tracks the player until `aimLockMs`, then freezes the
      // aim for the charge window, then fires (active) along the frozen line.
      aimPlayer: true,
      aimReach: reach,
      aimLockMs,
    };
  });
}

/** Attack #3 — laser turret lock-on (1–4 turrets by difficulty). */
export const TURRET: Pattern = {
  id: 'TURRET',
  family: 'turret',
  spawn: makeTurrets,
};
